/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect} from 'react';
import {computed, ReadonlySignal, signal} from '@preact/signals-react';
import {useSignals} from '@preact/signals-react/runtime';

/**
 * Defines the stable ids of the current theme Asset Details Drawer's
 * accordion sections - one per container component that renders one.
 */
export enum AssetDetailsSection {
  assetDetails = 'assetDetails',
  vision = 'vision',
  spreader = 'spreader',
  diagnostics = 'diagnostics',
  tags = 'tags',
  additionalInfo = 'additionalInfo',
  smarterTrailer = 'smarterTrailer',
  genericSensor = 'genericSensor',
  // prefixes each SC device sensor graph's own section id ...
  scSensor = 'scSensor'
}

/**
 * Stores the expanded state of the Asset Details Drawer's accordions keyed
 * by section id. A section id is present in this map only while its
 * accordion is actually mounted - which varies per asset, since Vision,
 * Spreader and Diagnostics each render conditionally - so the header's bulk
 * expand/collapse control only ever reasons about sections the selected
 * asset really has.
 *
 * This is a single, module-scope store shared by whatever Asset Details
 * Drawer is currently mounted - it assumes only one drawer is ever open at
 * a time (true today: the app shows a single tracking side sheet). A second
 * concurrently-mounted drawer would collide on these same section ids; if
 * that ever becomes a real scenario, this store needs to be keyed by
 * drawer/asset instance id rather than section id alone.
 */
export const assetDetailsExpandedSections = signal<Record<string, boolean>>({});

// per-section `computed()` cache, lazily created - `useRegisteredAccordion`
// reads a section's own computed instead of the whole `assetDetailsExpandedSections`
// map directly, so toggling one section only re-renders that section's own
// component: `computed()` only notifies its subscribers when ITS OWN produced
// value changes, so a sibling section's computed (same boolean as before)
// never re-notifies even though the underlying map signal changed ...
const sectionExpandedComputedCache: Record<string, ReadonlySignal<boolean | undefined>> = {};

// each registered section's own starting state, so a newly selected asset's
// drawer opens every section the way it first did ...
const sectionDefaultExpanded: Record<string, boolean> = {};

const getSectionExpandedComputed = (id: string) => {
  if (!sectionExpandedComputedCache[id]) {
    sectionExpandedComputedCache[id] = computed(() => assetDetailsExpandedSections.value[id]);
  }
  return sectionExpandedComputedCache[id];
};

/**
 * Replaces the expanded state of every mounted Asset Details Drawer accordion.
 *
 * @param sections Defines the expanded state keyed by accordion section id.
 */
export const setAssetDetailsExpandedSections = (sections: Record<string, boolean>) => {
  assetDetailsExpandedSections.value = sections;
};

/**
 * Gets the expanded state of every mounted Asset Details Drawer accordion.
 *
 * @returns Expanded state keyed by mounted accordion section id.
 */
export const getAssetDetailsExpandedSections = () => assetDetailsExpandedSections.value;

/**
 * Derives whether every currently mounted Asset Details Drawer accordion is
 * expanded. Sections that are not mounted for the selected asset are absent
 * from the underlying map entirely, so this can never be held `false` by a
 * section the asset does not have; an empty drawer is reported as not
 * expanded so the header's control offers "expand all" first.
 *
 * @returns `true` when at least one section is mounted and all mounted
 *          sections are expanded, otherwise `false`.
 */
export const assetDetailsAllExpanded = computed(() => {
  const expandedStates = Object.values(assetDetailsExpandedSections.value);
  return expandedStates.length > 0 && expandedStates.every(Boolean);
});

/**
 * Registers an Asset Details Drawer accordion section with its initial
 * expanded state. Re-registering an already registered section is ignored so
 * a re-render never discards the state the user has since chosen.
 *
 * @param id              Defines the accordion section id.
 * @param defaultExpanded Defines whether the section starts expanded.
 */
export const registerAssetDetailsSection = (id: string, defaultExpanded: boolean = false) => {
  const sections = assetDetailsExpandedSections.value;
  if (!(id in sections)) {
    sectionDefaultExpanded[id] = defaultExpanded;
    setAssetDetailsExpandedSections({...sections, [id]: defaultExpanded});
  }
};

/**
 * Unregisters an Asset Details Drawer accordion section, removing it from
 * the bulk expand/collapse computation.
 *
 * @param id Defines the accordion section id.
 */
export const unregisterAssetDetailsSection = (id: string) => {
  const sections = assetDetailsExpandedSections.value;
  if (id in sections) {
    const remainingSections = {...sections};
    delete remainingSections[id];
    setAssetDetailsExpandedSections(remainingSections);
  }
};

/**
 * Replaces a single Asset Details Drawer accordion section's expanded state,
 * leaving every other mounted section untouched. Unregistered ids are
 * ignored rather than implicitly registered.
 *
 * @param id       Defines the accordion section id.
 * @param expanded Defines whether the section is expanded.
 */
export const setAssetDetailsSectionExpanded = (id: string, expanded: boolean) => {
  const sections = assetDetailsExpandedSections.value;
  if (id in sections) {
    setAssetDetailsExpandedSections({...sections, [id]: expanded});
  }
};

/**
 * Replaces the expanded state of every currently mounted Asset Details
 * Drawer accordion section at once.
 *
 * @param expanded Defines whether every mounted section is expanded.
 */
export const setAllAssetDetailsSectionsExpanded = (expanded: boolean) => {
  const sections = assetDetailsExpandedSections.value;
  setAssetDetailsExpandedSections(Object.keys(sections).reduce((nextSections, id) => ({
    ...nextSections,
    [id] : expanded
  }), {} as Record<string, boolean>));
};

/**
 * Returns every currently mounted Asset Details Drawer accordion section to
 * the expanded state it registered with, so the sections the user opened or
 * closed for one asset are not carried over to the next asset selected.
 */
export const resetAssetDetailsSections = () => {
  const sections = assetDetailsExpandedSections.value;
  setAssetDetailsExpandedSections(Object.keys(sections).reduce((nextSections, id) => ({
    ...nextSections,
    [id] : Boolean(sectionDefaultExpanded[id])
  }), {} as Record<string, boolean>));
};

/**
 * Expands every currently mounted Asset Details Drawer accordion section, or
 * collapses them all when they are already expanded.
 */
export const toggleAllAssetDetailsSections = () => {
  setAllAssetDetailsSectionsExpanded(!assetDetailsAllExpanded.value);
};

/**
 * Tracks one Asset Details Drawer accordion section's expanded state in the
 * shared store, registering it on mount and unregistering it on unmount so
 * the header's bulk control always matches what the selected asset renders.
 * State changes whenever the user toggles this section's own header or uses
 * the drawer header's expand/collapse-all control.
 *
 * ```typescript
 *
 *   import {
 *     AssetDetailsSection,
 *     useRegisteredAccordion
 *   } from '@/routes/tracking/assetDetailsAccordionStore';
 *   ...
 *
 *   const {expanded, onExpand} = useRegisteredAccordion(
 *     AssetDetailsSection.diagnostics,
 *     false,
 *     isCurrentTheme()
 *   );
 *
 * ```
 *
 * @param id              Defines the accordion section id.
 * @param defaultExpanded Defines whether the section starts expanded.
 * @param isRegistered    Defines whether the section is actually rendered -
 *                        pass the same condition the caller's current theme
 *                        branch renders on so legacy markup never registers.
 * @returns The section's `expanded` state and its `onExpand` setter, ready
 *          to pass straight to a current theme accordion component.
 */
export const useRegisteredAccordion = (
  id: string,
  defaultExpanded: boolean = false,
  isRegistered: boolean = true
) => {
  useSignals();
  // reads this section's own computed rather than the whole
  // `assetDetailsExpandedSections` map, so toggling a sibling section never
  // re-renders this one (see `getSectionExpandedComputed`'s own comment) ...
  const sectionExpanded = getSectionExpandedComputed(id).value;

  useEffect(() => {
    if (isRegistered) {
      registerAssetDetailsSection(id, defaultExpanded);
    }
    return function() {
      unregisterAssetDetailsSection(id);
    };
  }, [id, isRegistered]);

  const onExpand = useCallback((expanded: boolean) => {
    setAssetDetailsSectionExpanded(id, expanded);
  }, [id]);

  return {
    // falls back to defaultExpanded for the render that precedes this
    // section's own registration effect, so the accordion never flashes
    // collapsed before the store knows about it ...
    expanded : sectionExpanded === undefined ? defaultExpanded : sectionExpanded,
    onExpand
  };
};
